import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import runtimeEnv from "@mars/heroku-js-runtime-env";
import querystring from "query-string";
import AsyncSelect from "react-select/async";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";

// This is mostly boilerplate code
// taken from the react-select demo here:
// https://material-ui.com/demos/autocomplete/

const styles = (theme) => ({
  input: {
    display: "flex",
    backgroundColor: "#ffffff",
    color: theme.palette.text.primary,
    "& input": {
      font: "inherit",
    },
  },
  valueContainer: {
    display: "flex",
    flexWrap: "nowrap",
    flex: 1,
    alignItems: "center",
  },
  singleValue: {
    fontSize: 16,
    wordBreak: "break-all",
  },
  noOptionsMessage: {
    padding: theme.spacing(1, 2),
  },
  loadingMessage: {
    padding: theme.spacing(1, 2),
  },
  paper: {
    position: "absolute",
    zIndex: theme.zIndex.snackbar,
    marginTop: 0,
    left: 0,
    right: 0,
  },
});

const inputComponent = ({ inputRef, ...props }) => (
  <div ref={inputRef} {...props} />
);

inputComponent.propTypes = {
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]),
};

const Control = (props) => {
  // Override shrink prop ONLY if select box has value
  const inputLabelProps = {};
  if (props.hasValue) {
    inputLabelProps.shrink = true;
  }
  // if (props.hasValue) {
  return (
    <TextField
      variant="outlined"
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          ref: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      InputLabelProps={inputLabelProps}
      {...props.selectProps.textFieldProps}
    />
  );
};

Control.propTypes = {
  hasValue: PropTypes.bool.isRequired,
  /**
   * Children to render.
   */
  children: PropTypes.node,
  /**
   * The mouse down event and the innerRef to pass down to the controller element.
   */
  innerProps: PropTypes.shape({
    onMouseDown: PropTypes.func.isRequired,
  }).isRequired,
  innerRef: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]).isRequired,
  selectProps: PropTypes.object.isRequired,
};

const Option = (props) => (
  <ListItem
    button
    buttonRef={props.innerRef}
    selected={props.isFocused}
    component="div"
    style={{
      fontWeight: props.isSelected ? 500 : 400,
    }}
    {...props.innerProps}
  >
    <ListItemText
      primary={props.data.mainText}
      secondary={props.data.secondaryText}
    />
  </ListItem>
);

Option.propTypes = {
  data: PropTypes.object.isRequired, //custom
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * props passed to the wrapping element for the group.
   */
  innerProps: PropTypes.shape({
    id: PropTypes.string.isRequired,
    key: PropTypes.string, //Removed isRequired
    onClick: PropTypes.func.isRequired,
    onMouseMove: PropTypes.func.isRequired,
    onMouseOver: PropTypes.func.isRequired,
    tabIndex: PropTypes.number.isRequired,
  }).isRequired,
  /**
   * Inner ref to DOM Node
   */
  innerRef: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.func,
    PropTypes.shape({
      current: PropTypes.any.isRequired,
    }),
  ]), //Removed isRequired
  /**
   * Whether the option is focused.
   */
  isFocused: PropTypes.bool.isRequired,
  /**
   * Whether the option is selected.
   */
  isSelected: PropTypes.bool.isRequired,
};

// Empty placeholder since we're using a shrinking label
// eslint-disable-next-line no-unused-vars
const Placeholder = (props) => {
  return null;
};

const SingleValue = (props) => {
  const { color } = props.getStyles("singleValue", props);
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      style={{ color: color }}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
};

SingleValue.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * Props passed to the wrapping element for the group.
   */
  innerProps: PropTypes.any, //removed isRequired
  selectProps: PropTypes.object.isRequired,
  getStyles: PropTypes.any,
};

const ValueContainer = (props) => (
  <div className={props.selectProps.classes.valueContainer}>
    {props.children}
  </div>
);

ValueContainer.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  selectProps: PropTypes.object.isRequired,
};

const Menu = (props) => (
  <Paper
    square
    className={props.selectProps.classes.paper}
    {...props.innerProps}
  >
    {props.children}
  </Paper>
);

Menu.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.element.isRequired,
  /**
   * Props to be passed to the menu wrapper.
   */
  innerProps: PropTypes.object.isRequired,
  selectProps: PropTypes.object.isRequired,
};

const NoOptionsMessage = (props) => (
  <Typography
    align="center"
    color="textSecondary"
    className={props.selectProps.classes.noOptionsMessage}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);

NoOptionsMessage.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * Props to be passed on to the wrapper.
   */
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
};

const LoadingMessage = (props) => (
  <Typography
    align="center"
    color="textSecondary"
    className={props.selectProps.classes.loadingMessage}
    {...props.innerProps}
  >
    {props.children}
  </Typography>
);

LoadingMessage.propTypes = {
  /**
   * The children to be rendered.
   */
  children: PropTypes.node,
  /**
   * Props to be passed on to the wrapper.
   */
  innerProps: PropTypes.object,
  selectProps: PropTypes.object.isRequired,
};

const components = {
  Control,
  Menu,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  NoOptionsMessage,
  LoadingMessage,
};

class LocationSelect extends Component {
  constructor(props) {
    super(props);

    this.state = {
      value: "",
    };

    this.lastRequestId = null;
  }

  // Triggers when an option is selected
  // Weird hack to keep things consistent outside of this component
  handleChange = (selectedOption) => {
    const e = { target: { value: selectedOption, name: this.props.id } };
    this.props.onChange(e);
  };

  // Triggers when the search input changes
  handleInputChange = (inputValue) => {
    this.setState({
      value: inputValue,
    });
  };

  // Written like this to easily test location searching
  getLocationSearchResults = (inputValue) => {
    // Compose request url with search text
    return axios
      .get(
        runtimeEnv().REACT_APP_API_URL +
          "/locations/search?" +
          querystring.stringify({ input: inputValue })
      )
      .then((response) => {
        // return locations from API
        return response.data;
      })
      .catch((error) => {
        console.log(error); //eslint-disable-line no-console
        console.log(error.response); //eslint-disable-line no-console
        // return empty locations array (for the sake of returning things)
        return [];
      });
  };

  loadOptions = (inputValue, callback) => {
    // Cancel the previous request every time this is called
    if (this.lastRequestId !== null) {
      clearTimeout(this.lastRequestId);
    }
    this.lastRequestId = setTimeout(() => {
      this.getLocationSearchResults(inputValue).then((results) => {
        // Map locations to required format
        let locations = results.map((result) => ({
          value: result.placeId,
          label: result.description,
          mainText: result.mainText,
          secondaryText: result.secondaryText,
        }));
        callback(locations);
      });
    }, 300);
  };

  render() {
    // Throw all other props into "other"
    const { classes, theme, id, value, isDisabled, textFieldProps, className } =
      this.props;
    const inputId = id + "-input";

    const selectStyles = {
      singleValue: (base, state) => {
        // Dynamically set color of the value based on isDisabled since it's a typography component
        // and doesn't have a disabled color built in
        const color = state.isDisabled
          ? theme.palette.text.disabled
          : theme.palette.text.primary;
        return { ...base, color };
      },
    };

    return (
      <div className={className}>
        <AsyncSelect
          id={id}
          classes={classes}
          styles={selectStyles}
          components={components}
          // So much value being created here
          // Conditionally set the value or there will be
          // weird styling/functionality issues with the input label/clear button
          value={value && value.label && value.value && value}
          isClearable
          cacheOptions
          // Needs to be formatted as a function
          noOptionsMessage={() => "Type a business name or address..."}
          loadOptions={this.loadOptions}
          onChange={this.handleChange}
          isDisabled={isDisabled}
          inputId={inputId}
          inputValue={this.state.value}
          onInputChange={this.handleInputChange}
          textFieldProps={textFieldProps}
        />
      </div>
    );
  }
}

LocationSelect.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  theme: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  value: PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
  }),
  isDisabled: PropTypes.bool, // not required
  textFieldProps: PropTypes.object, // Will error if props are invalid for TextField component
  onChange: PropTypes.func.isRequired,
};

export default withStyles(styles, { withTheme: true })(LocationSelect);
